// chatbotIntentMatcher.js
//
// Recherche, dans la base de connaissances dynamique (ChatbotIntent,
// gérée depuis le Chatbot Dashboard), l'intent qui correspond le mieux au
// message de l'utilisateur, puis choisit une réponse au hasard parmi
// `intent.responses`. Même logique de scoring (mots-clés/synonymes/fautes
// de frappe + distance de Levenshtein) que supportChatAnswers.js, pour que
// les deux systèmes se comportent de façon cohérente.

const CONFIDENCE_THRESHOLD = 4;

const stripAccents = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

export const normalizeIntentMessage = (value = "") =>
  stripAccents(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const levenshteinDistance = (a = "", b = "") => {
  if (Math.abs(a.length - b.length) > 2) return 99;
  const matrix = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j += 1) matrix[0][j] = j;
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      matrix[i][j] = a[i - 1] === b[j - 1]
        ? matrix[i - 1][j - 1]
        : Math.min(matrix[i - 1][j - 1], matrix[i][j - 1], matrix[i - 1][j]) + 1;
    }
  }
  return matrix[a.length][b.length];
};

// Coeur du scoring mots-clés (exact/sous-chaine/mot exact/mot approchant via
// Levenshtein). Extrait de scoreIntent pour que ce même scoring serve aussi a
// noter les mots-cles propres a une image/video/lien (pickBestAttachment),
// sans dupliquer une troisieme fois cette logique (supportChatAnswers.js a
// deja sa propre copie, distincte par necessite de layering).
export const scoreByKeywords = (keywords, normalizedMessage, words) => {
  const terms = (keywords || []).map(normalizeIntentMessage).filter(Boolean);

  return terms.reduce((score, term) => {
    if (normalizedMessage === term) return score + 12;
    if (normalizedMessage.includes(term)) return score + 8;
    const termWords = term.split(" ");
    const exactWordScore = termWords.some((word) => words.includes(word)) ? 4 : 0;
    const fuzzyWordScore = termWords.some((termWord) => (
      termWord.length >= 5 &&
      words.some((word) => word.length >= 5 && levenshteinDistance(word, termWord) <= 2)
    )) ? 2 : 0;
    return score + exactWordScore + fuzzyWordScore;
  }, 0);
};

const scoreIntent = (intent, normalizedMessage, words) => {
  const allTerms = [
    ...(intent.keywords || []),
    ...(intent.synonyms || []),
    ...(intent.misspellings || [])
  ];
  return scoreByKeywords(allTerms, normalizedMessage, words);
};

// Quand un intent a plusieurs images/videos, choisit celle(s) dont les
// mots-cles propres au fichier matchent le mieux le message de
// l'utilisateur. Si AUCUN fichier n'a de score > 0 (aucun mot-cle de fichier
// ne correspond), on revient au comportement actuel : montrer tous les
// fichiers, sans filtrage - c'est un choix explicite, pas un oubli : on ne
// veut jamais masquer une piece jointe par defaut faute de mots-cles.
export const pickBestAttachment = (items, normalizedMessage, words) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  const scored = items.map((item) => ({
    item,
    score: scoreByKeywords(item?.keywords, normalizedMessage, words)
  }));
  const best = Math.max(...scored.map((entry) => entry.score));
  if (best <= 0) return items;
  return scored.filter((entry) => entry.score === best).map((entry) => entry.item);
};

// Verifie la liste de mots-cles de contact PROPRE a un intent (distincte de
// la liste globale de supportChatAnswers.js -> isContactRequest). Les deux
// signaux sont combines en OR cote appelant (userMessage.js) : un intent
// sans contactKeywords ne retire jamais la couverture du systeme global.
export const intentMatchesContactKeywords = (message = "", intent) => {
  const keywords = intent?.contactKeywords;
  if (!Array.isArray(keywords) || keywords.length === 0) return false;
  const normalized = normalizeIntentMessage(message);
  if (!normalized) return false;
  const words = normalized.split(" ").filter(Boolean);
  return scoreByKeywords(keywords, normalized, words) > 0;
};

// Bonus de correspondance de role : volontairement beaucoup plus grand que
// n'importe quel ecart de recence possible, pour que la pertinence de role
// gagne TOUJOURS face a la recence (la recence ne depart les ex-aequo qu'a
// l'interieur d'un meme palier de pertinence de role).
const ROLE_MATCH_BONUS = 1000000;

/**
 * Deuxieme niveau de suggestions, affiche separement des suggestions de
 * l'intent matche : cherche dans TOUTES les categories du chatbot (pas
 * seulement celle en cours), priorise par le role du client connecte, trie
 * par recence (createdAt). 100% cote client : tous les intents sont deja
 * charges en memoire pour le matching habituel.
 * @param {Array} intents
 * @param {{excludeCategory?: string, customerRole?: string, limit?: number}} options
 */
export const getCrossCategorySuggestions = (intents, options = {}) => {
  const { excludeCategory = "", customerRole = "", limit = 3 } = options;
  if (!Array.isArray(intents) || intents.length === 0) return [];

  const role = String(customerRole || "").toLowerCase();

  const ranked = intents
    .filter((intent) => intent.enabled !== false && intent.category !== excludeCategory)
    .map((intent) => {
      const roleMatches = Boolean(role) && (
        String(intent.category || "").toLowerCase() === role ||
        (intent.responsibleRole || []).some((r) => String(r).toLowerCase() === role)
      );
      const recency = new Date(intent.createdAt || 0).getTime();
      return {
        intent,
        score: (roleMatches ? ROLE_MATCH_BONUS : 0) + recency
      };
    })
    .sort((a, b) => b.score - a.score);

  return ranked.slice(0, limit).map((entry) => entry.intent);
};

/**
 * @param {string} message - message utilisateur déjà nettoyé (sanitizeChatMessage)
 * @param {Array} intents - intents pour la langue courante (enabled !== false)
 * @param {string} contextTopicId - catégorie/topic du fil de conversation en cours (bonus léger)
 * @returns {{ intent: object|null, score: number, confident: boolean }}
 */
export const matchIntent = (message = "", intents = [], contextTopicId = "") => {
  const normalized = normalizeIntentMessage(message);
  if (!normalized || !Array.isArray(intents) || intents.length === 0) {
    return { intent: null, score: 0, confident: false };
  }

  const words = normalized.split(" ").filter(Boolean);
  const ranked = intents
    .filter((intent) => intent.enabled !== false)
    .map((intent) => ({
      intent,
      score: scoreIntent(intent, normalized, words) +
        (intent.category === contextTopicId ? 2 : 0) +
        (Number(intent.priority) || 0) * 0.5
    }))
    .sort((a, b) => b.score - a.score);

  if (ranked[0]?.score >= CONFIDENCE_THRESHOLD) {
    return { intent: ranked[0].intent, score: ranked[0].score, confident: true };
  }

  return { intent: null, score: ranked[0]?.score || 0, confident: false };
};

// Convertit la liste "Find Responsible Person" (Chatbot Dashboard) en un
// Set des messageKey actives, pour une recherche O(1) a chaque message
// plutot qu'un .some()/.find() reparcourant tout le tableau a chaque appel.
// A calculer UNE FOIS (useMemo) quand les parametres changent, jamais a
// chaque render de message.
export const buildEnabledResponsibleKeysSet = (messageKeys = []) =>
  new Set(
    (Array.isArray(messageKeys) ? messageKeys : [])
      .filter((item) => item?.enabled !== false)
      .map((item) => normalizeIntentMessage(item?.key || "").replace(/ /g, "_"))
      .filter(Boolean)
  );

/**
 * Decide si le bouton "Find a Responsible Person" doit s'afficher sous un
 * message bot donne, a partir des parametres "Find Responsible Person"
 * configures dans le Chatbot Dashboard (voir findResponsiblePerson renvoye
 * par GET /chatbot/intents et buildEnabledResponsibleKeysSet ci-dessus).
 * @param {string} messageKey - messageKey porte par le message bot (intent.messageKey)
 * @param {{enabled: boolean, enabledKeysSet: Set<string>}} settings - deja charges/derives une seule fois par l'appelant
 */
export const shouldShowResponsibleButton = (messageKey, settings) => {
  if (!messageKey || !settings?.enabled) return false;
  if (!(settings.enabledKeysSet instanceof Set)) return false;
  const normalized = normalizeIntentMessage(messageKey).replace(/ /g, "_");
  return settings.enabledKeysSet.has(normalized);
};

export const pickRandomResponse = (intent) => {
  const responses = Array.isArray(intent?.responses) ? intent.responses.filter(Boolean) : [];
  if (!responses.length) return "";
  return responses[Math.floor(Math.random() * responses.length)];
};
